import { API } from "@swivl-mono/api-helper";
import { OmniDashboard, OmniDashboardView } from "@swivl-mono/types";
import { LayoutWithSidebar, Spinner } from "@swivl-mono/ui";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import ErrorView from "../../Components/ErrorView/ErrorView";
import { SearchableSidebarLinksList } from "../../Components/SidebarLink/SearchableSidebarLinksList";
import { RouterWorksheetData } from "../../Components/Worksheets/Types/Worksheet.Interface";
import { usePropertiesGetLocationIds } from "../../Hooks/usePropertiesGetLocationIds";
import { ReportsDashboard } from "./Pages/Reports.Dashboard";
import { slugifyFolder } from "./Reports.Helpers";

export function ReportsView() {
  const [dashboards, setDashboards] = useState<OmniDashboard[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const page = usePropertiesGetLocationIds();
  const { dashboardId } = useParams<{ dashboardId: string }>();
  // Which reports show depends on which Property Manager version is loaded (ENG-3359),
  // not on the org's migration status — an org can be running both at once.
  const view: OmniDashboardView = page.preamble.startsWith("/properties-v3") ? "snowy" : "legacy";

  useEffect(() => {
    API.Omni.getDashboards(view)
      .then(setDashboards)
      .catch((err) => setError(err.message || "Failed to load dashboards"));
  }, [view]);

  const sidebarData: RouterWorksheetData[] = useMemo(() => {
    if (!dashboards) return [];

    const folderMap = new Map<string, OmniDashboard[]>();
    dashboards.forEach((d) => {
      const existing = folderMap.get(d.folder) || [];
      existing.push(d);
      folderMap.set(d.folder, existing);
    });

    return Array.from(folderMap.entries()).map(([folderName, folderDashboards]) => ({
      to: slugifyFolder(folderName),
      label: folderName,
      children: folderDashboards.map((d) => ({
        to: d.id,
        label: d.name
      }))
    }));
  }, [dashboards]);

  const selectedDashboard = useMemo(() => {
    if (!dashboards || !dashboardId) return null;
    return dashboards.find((d) => d.id === dashboardId) || null;
  }, [dashboards, dashboardId]);

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <ErrorView />
      </div>
    );
  }

  if (!dashboards) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <LayoutWithSidebar
      sidebar={<SearchableSidebarLinksList items={sidebarData} prefix={`${page.preamble}/page/reports`} page={page} />}
      contentNoPadding={true}
      content={
        selectedDashboard ? (
          <ReportsDashboard dashboardId={selectedDashboard.id} title={selectedDashboard.name} />
        ) : (
          <div className="flex h-full items-center justify-center text-gray-500">Please select a dashboard from the sidebar to get started!</div>
        )
      }
    />
  );
}
